// One recipe language for synthesised sounds (suggestion "one recipe language, baked or live"; audio cards 2, 5 and
// 14). A recipe is data: a list of layers (a filtered noise burst, a tone with a glide, a modal resonator bank, a grain
// cloud, crackle, an arpeggio of struck notes, a plucked string, a pad, and the bed textures: wind, rain, insects or
// birds, running water, a crowd's murmur) plus a length, a sample rate, a variant count and a finishing chain. The
// game writes its cues as recipes in its content; the package renders them. Every sound is "a noise transient + a
// tonal body with a two-stage decay"; variants come from the cue's seed (FNV-1a of its id), so a variant sounds the
// same every session and a test can compare it. Instruments (card 3, the music pass): a two-operator FM voice (an
// electric piano, a bell), a warm pad (detuned band-limited saws, a sub, a slowly breathing filter) and a plucked
// string with a pick position and body resonance; `renderSound`'s `transpose` renders one recipe at another pitch, so
// an instrument is a multisample.
import { dcBlock, env, fades, loopSeam, midiHz, noiseSource, normalise, Svf, trimTail, valueNoise, variantRng, type NoiseColour, type Rng } from "./dsp";

/** A filtered noise burst: `f0` swept towards `f1` over `sweep` seconds. */
export interface NoiseLayer {
  type: "noise";
  colour?: NoiseColour;
  filter: "lp" | "bp" | "hp";
  f0: number;
  f1?: number;
  sweep?: number;
  q?: number;
  att: number;
  tau: number;
  amp: number;
  at?: number;
}
/** A tone: a sine (plus harmonics) gliding from `f0` to `f1` with time constant `glide`, with optional vibrato. */
export interface ToneLayer {
  type: "tone";
  f0: number;
  f1?: number;
  glide?: number;
  harm?: number[];
  att: number;
  tau: number;
  amp: number;
  at?: number;
  /** Vibrato: rate (Hz), depth (semitones) and the seconds it takes to fade in (default 0.12). */
  vibrato?: [number, number] | [number, number, number];
}
/** A bank of decaying resonators: `[frequency, amplitude, tau]` each (wood, stone, metal, bells, UI ticks). */
export interface ModalLayer {
  type: "modal";
  partials: [number, number, number][];
  amp: number;
  at?: number;
}
/** A cloud of short band-passed noise grains (grass, rustle, fizz); density falls as (1 − u)^pow over the span. */
export interface GrainLayer {
  type: "grains";
  count: number;
  fLo: number;
  fHi: number;
  /** Grain length range (s). */
  dur: [number, number];
  span: number;
  amp: number;
  at?: number;
  pow?: number;
}
/** Poisson-timed resonant clicks (gravel, fire, debris). */
export interface CrackleLayer {
  type: "crackle";
  rate: number;
  fLo: number;
  fHi: number;
  span: number;
  amp: number;
  at?: number;
}
/** Struck notes in sequence (a chime, a stinger, a level-up arpeggio): MIDI notes `step` seconds apart. */
export interface NotesLayer {
  type: "notes";
  notes: number[];
  step: number;
  voice: "bell" | "pluck" | "soft";
  tau: number;
  amp: number;
  at?: number;
}
/** A Karplus–Strong plucked string at `f0` (an instrument note). */
export interface PluckLayer {
  type: "pluck";
  f0: number;
  /** Feedback loss per period (0.990 to 0.999: longer sustain). */
  decay: number;
  /** 0 dark … 1 bright (the pick's noise filter). */
  bright: number;
  amp: number;
  at?: number;
  /** Where along the string it is plucked (0.05 to 0.5): a comb on the excitation, hollower near the middle. */
  position?: number;
  /** Body resonance: two band-passes (about 220 and 480 Hz, a guitar's or harp's box) mixed in at this level. */
  body?: number;
}
/** A two-operator FM voice: a sine carrier at `f0` phase-modulated by a sine at `ratio` × f0 whose index falls from
 *  `index` with time constant `indexTau` towards `indexEnd`; a two-stage decay (a fast `tau1` share `split`, then
 *  `tau`); an optional tine (a short partial at `tine[0]` × f0, level `tine[1]`, decay `tine[2]`). Electric pianos
 *  (ratio 1, a tine at 14), bells (ratio 1.4 or 3.5). `keytrack` shortens the decays for higher notes:
 *  × (262 Hz / f)^keytrack. */
export interface FmLayer {
  type: "fm";
  f0: number;
  ratio: number;
  index: number;
  indexTau: number;
  indexEnd?: number;
  att: number;
  tau: number;
  tau1?: number;
  split?: number;
  tine?: [number, number, number];
  keytrack?: number;
  /** A gentle tremolo: rate (Hz) and depth (0..1). */
  tremolo?: [number, number];
  amp: number;
  at?: number;
}
/** A warm pad note: `voices` band-limited saws spread over ± `detune` cents with random phases, a sine sub an octave
 *  down at `sub`, a two-pole low-pass at `cutoff` opening over the attack and breathing with a slow LFO (`lfo`: rate
 *  Hz, depth 0..1), a touch of band-passed air noise. Sustains to the end of the render (an instrument loops its
 *  middle). */
export interface WarmPadLayer {
  type: "warmpad";
  f0: number;
  voices: number;
  detune: number;
  cutoff: number;
  q?: number;
  sub?: number;
  air?: number;
  lfo?: [number, number];
  att: number;
  amp: number;
  at?: number;
}
/** A pad note: detuned saws through a low-pass, slow attack and release (an instrument note). */
export interface PadLayer {
  type: "pad";
  f0: number;
  att: number;
  rel: number;
  hold: number;
  cutoff: number;
  amp: number;
  at?: number;
}
/** Wind: pink noise through a band-pass wandering with gusts, a low rumble and an optional whistle (a bed). */
export interface WindLayer {
  type: "wind";
  centre: number;
  spread: number;
  gust: number;
  whistle?: number;
  amp: number;
}
/** Rain: a hiss bed plus band-passed drop ticks at `drops` per second (a bed). */
export interface RainLayer {
  type: "rain";
  drops: number;
  hiss: number;
  amp: number;
}
/** Insects or birds: `count` callers, each repeating chirps of `pulses` pulses at `pulseHz` every `period` seconds
 *  (crickets), or sweeping notes when `sweep` is set (birdsong). */
export interface ChirpLayer {
  type: "chirps";
  count: number;
  fLo: number;
  fHi: number;
  pulses: [number, number];
  pulseHz: number;
  period: [number, number];
  /** Pitch sweep across a pulse (fraction: 0.4 = up 40 %); birds sweep, insects don't. */
  sweep?: number;
  amp: number;
}
/** Running water: a low slosh plus bubbles (rising blips) at `bubbles` per second (a bed). */
export interface WaterLayer {
  type: "water";
  bubbles: number;
  amp: number;
}
/** A crowd's murmur: band-passed noise shaped by syllable-rate envelopes of a few voices (a bed). */
export interface MurmurLayer {
  type: "murmur";
  voices: number;
  amp: number;
}

/** Any layer. */
export type Layer = NoiseLayer | ToneLayer | ModalLayer | GrainLayer | CrackleLayer | NotesLayer | PluckLayer | PadLayer | FmLayer | WarmPadLayer | WindLayer | RainLayer | ChirpLayer | WaterLayer | MurmurLayer;

/** A sound as data. */
export interface SoundRecipe {
  layers: Layer[];
  /** Seconds rendered (before the tail trim). */
  length: number;
  /** Variants (each its own seed); default 1. */
  vars?: number;
  /** Sample rate (default 32 kHz; beds 16 to 22 kHz, bright cues 44.1 kHz). */
  sr?: number;
  /** Two decorrelated channels (beds). */
  stereo?: boolean;
  /** A loop: the seam's crossfade (s). Loops are normalised "long" and not trimmed. */
  loop?: number;
  /** Frequency spread between variants (fraction, default 0.04). */
  jitter?: number;
  /** Loudness target (dBFS; default −16 one-shots, −20 loops), or "none" to keep the raw level. */
  level?: number | "none";
}

/** A rendered variant. */
export interface Rendered {
  sr: number;
  channels: Float32Array[];
}

const at = (l: { at?: number }, sr: number): number => Math.round((l.at ?? 0) * sr);
const lerp = (a: number, b: number, u: number): number => a + (b - a) * u;
const pickIn = (rng: Rng, r: [number, number]): number => lerp(r[0], r[1], rng());

function addNoise(out: Float32Array, sr: number, l: NoiseLayer, rng: Rng, fm: number): void {
  const src = noiseSource(rng, l.colour ?? "white");
  const f = new Svf(sr);
  const q = l.q ?? 0.8;
  const f0 = l.f0 * fm;
  const f1 = (l.f1 ?? l.f0) * fm;
  const sweep = l.sweep ?? 0.1;
  const s0 = at(l, sr);
  const n = Math.min(out.length - s0, Math.ceil((l.att + 7 * l.tau) * sr));
  // narrow white band-passes would vanish: compensate (card 2)
  const comp = l.filter === "bp" && (l.colour ?? "white") === "white" ? Math.min(12, Math.max(0.5, Math.sqrt(sr / 2 / (f0 / q)) * 0.5)) : 1;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    if ((i & 15) === 0) f.set(f0 * Math.pow(f1 / f0, Math.min(1, t / sweep)), q);
    const x = src();
    const y = l.filter === "lp" ? f.lp(x) : l.filter === "hp" ? f.hp(x) : f.bp(x);
    out[s0 + i]! += y * l.amp * comp * env(t, l.att, l.tau);
  }
}

function addTone(out: Float32Array, sr: number, l: ToneLayer, _rng: Rng, fm: number): void {
  const s0 = at(l, sr);
  const n = Math.min(out.length - s0, Math.ceil((l.att + 7 * l.tau) * sr));
  const f0 = l.f0 * fm;
  const f1 = (l.f1 ?? l.f0) * fm;
  const glide = l.glide ?? 0.05;
  const harm = [1, ...(l.harm ?? [])];
  const phase = harm.map(() => 0);
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    let f = f1 + (f0 - f1) * Math.exp(-t / glide);
    if (l.vibrato) f *= Math.pow(2, (l.vibrato[1] * Math.sin(2 * Math.PI * l.vibrato[0] * t) * Math.min(1, t / (l.vibrato[2] ?? 0.12))) / 12);
    let s = 0;
    for (let k = 0; k < harm.length; k++) {
      const fk = f * (k + 1);
      if (fk >= sr * 0.45) continue;
      phase[k] = (phase[k]! + fk / sr) % 1;
      s += Math.sin(2 * Math.PI * phase[k]!) * harm[k]!;
    }
    out[s0 + i]! += s * l.amp * env(t, l.att, l.tau);
  }
}

function addModal(out: Float32Array, sr: number, l: ModalLayer, rng: Rng, fm: number): void {
  const s0 = at(l, sr);
  const atk = Math.max(1, Math.round(0.0004 * sr));
  for (const [f0, a, tau] of l.partials) {
    const f = f0 * fm;
    if (f <= 0 || f >= sr * 0.45) continue;
    const w = (2 * Math.PI * f) / sr;
    const c = Math.cos(w);
    const s = Math.sin(w);
    const k = Math.exp(-1 / (tau * sr));
    const ph = rng() * 0.3;
    let re = Math.cos(ph);
    let im = Math.sin(ph);
    let g = a * l.amp;
    const n = Math.min(out.length - s0, Math.ceil(tau * 8 * sr));
    for (let i = 0; i < n; i++) {
      const r2 = re * c - im * s;
      im = re * s + im * c;
      re = r2;
      if ((i & 1023) === 1023) {
        const m = Math.hypot(re, im);
        re /= m;
        im /= m;
      }
      out[s0 + i]! += im * g * (i < atk ? i / atk : 1);
      g *= k;
    }
  }
}

function addGrains(out: Float32Array, sr: number, l: GrainLayer, rng: Rng, fm: number): void {
  const s0 = at(l, sr);
  const pow = l.pow ?? 1.5;
  for (let g = 0; g < l.count; g++) {
    // rejection-sample the start against the density (1 − u)^pow
    let u = rng();
    for (let tries = 0; tries < 8 && rng() > Math.pow(1 - u, pow); tries++) u = rng();
    const start = s0 + Math.round(u * l.span * sr);
    const len = Math.max(8, Math.round(pickIn(rng, l.dur) * sr));
    const f = Math.exp(lerp(Math.log(l.fLo), Math.log(l.fHi), rng())) * fm;
    const flt = new Svf(sr);
    flt.set(f, 2);
    const a = l.amp * Math.pow(rng(), 1.5) * 3;
    for (let i = 0; i < len && start + i < out.length; i++) {
      const w = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / len);
      out[start + i]! += flt.bp(rng() * 2 - 1) * a * w;
    }
  }
}

function addCrackle(out: Float32Array, sr: number, l: CrackleLayer, rng: Rng, fm: number): void {
  const s0 = at(l, sr);
  let t = 0;
  for (;;) {
    t += -Math.log(1 - rng() * 0.999) / l.rate;
    if (t > l.span) break;
    const start = s0 + Math.round(t * sr);
    const len = Math.round(0.0025 * sr);
    const f = Math.exp(lerp(Math.log(l.fLo), Math.log(l.fHi), rng())) * fm;
    const flt = new Svf(sr);
    flt.set(f, 4);
    const a = l.amp * (0.3 + 0.7 * rng()) * (1 - t / l.span);
    for (let i = 0; i < len * 3 && start + i < out.length; i++) out[start + i]! += flt.bp(i < len ? rng() * 2 - 1 : 0) * a * 4;
  }
}

function strike(out: Float32Array, sr: number, s0: number, f: number, voice: NotesLayer["voice"], tau: number, amp: number, rng: Rng): void {
  const parts: [number, number, number][] =
    voice === "bell"
      ? [[1, 1, tau], [2.76, 0.4, tau * 0.5], [5.4, 0.2, tau * 0.25], [8.93, 0.08, tau * 0.15]]
      : voice === "pluck"
        ? [[1, 1, tau], [2, 0.5, tau * 0.6], [3, 0.25, tau * 0.4], [4, 0.12, tau * 0.3]]
        : [[1, 1, tau], [2, 0.18, tau * 0.5]];
  addModal(out, sr, { type: "modal", partials: parts.map(([m, a, t]) => [f * m, a, t]), amp, at: s0 / sr }, rng, 1);
}

function addNotes(out: Float32Array, sr: number, l: NotesLayer, rng: Rng, pitch = 1): void {
  l.notes.forEach((m, i) => strike(out, sr, at(l, sr) + Math.round(i * l.step * sr), midiHz(m) * pitch, l.voice, l.tau, l.amp * (1 - i * 0.04), rng));
}

function addPluck(out: Float32Array, sr: number, l: PluckLayer, rng: Rng, fm: number): void {
  const s0 = at(l, sr);
  // the loop: a buffer of n samples averaged with its neighbour (the pair is n and n − 1 samples old: n − 0.5), and
  // an allpass in the loop adding the fraction left over (0.1 to 1.1 samples, where it is stable and flat), so the
  // loop is exactly a period long and the string is in tune (the first version's buffer of round(period − 0.5) ran
  // a sample short: sharp by 8 cents at D3, 32 at D5)
  const period = sr / (l.f0 * fm);
  const n = Math.max(2, Math.floor(period + 0.5 - 0.1));
  const frac = period + 0.5 - n;
  const c = (1 - frac) / (1 + frac);
  let apX = 0;
  let apY = 0;
  const buf = new Float32Array(n);
  const flt = new Svf(sr);
  flt.set(800 + l.bright * 7000, 0.7);
  for (let i = 0; i < n; i++) buf[i] = flt.lp(rng() * 2 - 1);
  if (l.position) {
    // the pick position: a comb on the excitation (x[i] − x[i + N·p]), then the mean taken out
    const k = Math.max(1, Math.round(n * Math.min(0.5, Math.max(0.02, l.position))));
    const src = buf.slice();
    let mean = 0;
    for (let i = 0; i < n; i++) {
      buf[i] = src[i]! - src[(i + k) % n]!;
      mean += buf[i]!;
    }
    mean /= n;
    for (let i = 0; i < n; i++) buf[i]! -= mean;
  }
  const body = l.body ?? 0;
  const b1 = new Svf(sr);
  const b2 = new Svf(sr);
  b1.set(220, 2.2);
  b2.set(480, 2.6);
  let idx = 0;
  for (let i = 0; s0 + i < out.length; i++) {
    const y = buf[idx]!;
    const nx = (idx + 1) % n;
    const v = l.decay * 0.5 * (y + buf[nx]!);
    apY = c * v + apX - c * apY;
    apX = v;
    buf[idx] = apY;
    idx = nx;
    out[s0 + i]! += (body ? y + body * (b1.bp(y) * 1.6 + b2.bp(y)) : y) * l.amp;
  }
}

const TAU = 2 * Math.PI;

function addFm(out: Float32Array, sr: number, l: FmLayer, _rng: Rng, fm: number): void {
  const s0 = at(l, sr);
  const f = l.f0 * fm;
  const kt = Math.pow(261.63 / f, l.keytrack ?? 0);
  const tau = l.tau * kt;
  const tau1 = (l.tau1 ?? l.tau * 0.15) * kt;
  const split = l.split ?? 0.5;
  const iTau = l.indexTau * kt;
  const iEnd = l.indexEnd ?? 0;
  const n = Math.min(out.length - s0, Math.ceil((l.att + 7 * tau) * sr));
  const wc = (TAU * f) / sr;
  const wm = (TAU * f * l.ratio) / sr;
  const tine = l.tine && f * l.tine[0] < sr * 0.45 ? l.tine : null;
  const wt = tine ? (TAU * f * tine[0]) / sr : 0;
  const tTau = tine ? tine[2] * kt : 1;
  let pc = 0;
  let pm = 0;
  let pt = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const u = t < l.att ? t / l.att : 1;
    const a = u * u * (3 - 2 * u);
    const ta = Math.max(0, t - l.att);
    const e = a * ((1 - split) * Math.exp(-ta / tau) + split * Math.exp(-ta / tau1));
    const idx = iEnd + (l.index - iEnd) * Math.exp(-t / iTau);
    let y = Math.sin(pc + idx * Math.sin(pm)) * e;
    if (tine) y += tine[1] * Math.sin(pt) * Math.exp(-t / tTau) * a;
    if (l.tremolo) y *= 1 - l.tremolo[1] * 0.5 * (1 - Math.cos(TAU * l.tremolo[0] * t));
    out[s0 + i]! += y * l.amp;
    pc += wc;
    pm += wm;
    pt += wt;
    if (pc > TAU) pc -= TAU;
    if (pm > TAU) pm -= TAU;
    if (pt > TAU) pt -= TAU;
  }
}

/** A band-limited saw sample (PolyBLEP) at phase `p` (0..1) advancing `dt` a sample. */
function blepSaw(p: number, dt: number): number {
  let y = 2 * p - 1;
  if (p < dt) {
    const x = p / dt;
    y -= x + x - x * x - 1;
  } else if (p > 1 - dt) {
    const x = (p - 1) / dt;
    y -= x * x + x + x + 1;
  }
  return y;
}

function addWarmPad(out: Float32Array, sr: number, l: WarmPadLayer, rng: Rng, fm: number): void {
  const s0 = at(l, sr);
  const f = l.f0 * fm;
  const v = Math.max(1, Math.round(l.voices));
  const det = Array.from({ length: v }, (_, k) => Math.pow(2, ((v === 1 ? 0 : (k / (v - 1)) * 2 - 1) * l.detune * (0.85 + 0.3 * rng())) / 1200));
  const ph = det.map(() => rng());
  const lp = new Svf(sr);
  const lp2 = new Svf(sr);
  const airF = new Svf(sr);
  airF.set(Math.min(f * 6, sr * 0.4), 1.2);
  const lfo = l.lfo ?? [0, 0];
  const lfoPh = rng() * TAU;
  const norm = 1 / Math.sqrt(v);
  let sub = 0;
  for (let i = 0; s0 + i < out.length; i++) {
    const t = i / sr;
    const u = Math.min(1, t / l.att);
    const e = Math.sin((u * Math.PI) / 2);
    if ((i & 31) === 0) {
      const c = l.cutoff * (0.45 + 0.55 * u) * (1 + lfo[1] * Math.sin(TAU * lfo[0] * t + lfoPh));
      lp.set(c, l.q ?? 0.7);
      lp2.set(c * 1.4, 0.6);
    }
    let s = 0;
    for (let k = 0; k < v; k++) {
      const dt = (f * det[k]!) / sr;
      let p = ph[k]! + dt;
      if (p >= 1) p -= 1;
      ph[k] = p;
      s += blepSaw(p, dt);
    }
    sub += f / 2 / sr;
    if (sub >= 1) sub -= 1;
    let y = lp2.lp(lp.lp(s * norm)) + (l.sub ?? 0) * Math.sin(TAU * sub);
    if (l.air) y += airF.bp(rng() * 2 - 1) * l.air;
    out[s0 + i]! += y * l.amp * e;
  }
}

function addPad(out: Float32Array, sr: number, l: PadLayer, _rng: Rng, fm: number): void {
  const s0 = at(l, sr);
  const f = l.f0 * fm;
  const det = [Math.pow(2, -7 / 1200), 1, Math.pow(2, 6 / 1200)];
  const ph = det.map((_, i) => i / 3);
  const flt = new Svf(sr);
  const total = l.att + l.hold + l.rel;
  for (let i = 0; s0 + i < out.length && i < total * sr; i++) {
    const t = i / sr;
    const e = t < l.att ? Math.sin(((t / l.att) * Math.PI) / 2) : t < l.att + l.hold ? 1 : Math.cos((((t - l.att - l.hold) / l.rel) * Math.PI) / 2);
    if ((i & 31) === 0) flt.set(l.cutoff * (0.5 + 0.5 * Math.min(1, t / l.att)), 0.8);
    let s = 0;
    for (let k = 0; k < det.length; k++) {
      ph[k] = (ph[k]! + (f * det[k]!) / sr) % 1;
      s += 2 * ph[k]! - 1;
    }
    out[s0 + i]! += flt.lp(s / 3) * l.amp * e;
  }
}

function addWind(out: Float32Array, sr: number, l: WindLayer, rng: Rng): void {
  const pink = noiseSource(rng, "pink");
  const brown = noiseSource(rng, "brown");
  const vn = valueNoise(rng, 0.35);
  const gust = valueNoise(rng, 0.22);
  const bp = new Svf(sr);
  const lp = new Svf(sr);
  lp.set(160, 0.7);
  const wh = new Svf(sr);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const g = 0.55 + 0.45 * gust(t) * l.gust;
    if ((i & 31) === 0) {
      bp.set(l.centre + l.spread * (vn(t) - 0.5) * 2 + 200 * g, 0.7);
      wh.set(1000 + 250 * vn(t + 3), 7);
    }
    let s = bp.bp(pink()) * g + lp.lp(brown()) * 0.5;
    if (l.whistle) s += wh.bp(pink()) * l.whistle * g * g;
    out[i]! += s * l.amp;
  }
}

function addRain(out: Float32Array, sr: number, l: RainLayer, rng: Rng): void {
  const pink = noiseSource(rng, "pink");
  const hp = new Svf(sr);
  hp.set(1800, 0.6);
  for (let i = 0; i < out.length; i++) out[i]! += hp.hp(pink()) * l.hiss * l.amp;
  const span = out.length / sr;
  let t = 0;
  for (;;) {
    t += -Math.log(1 - rng() * 0.999) / l.drops;
    if (t > span) break;
    const start = Math.round(t * sr);
    const f = Math.exp(lerp(Math.log(1500), Math.log(6500), rng()));
    const flt = new Svf(sr);
    flt.set(f, 3);
    const len = Math.round(0.004 * sr);
    const a = l.amp * (0.2 + 0.8 * rng() * rng());
    for (let i = 0; i < len * 4 && start + i < out.length; i++) out[start + i]! += flt.bp(i < len ? rng() * 2 - 1 : 0) * a * 3;
  }
}

function addChirps(out: Float32Array, sr: number, l: ChirpLayer, rng: Rng, fm: number): void {
  const span = out.length / sr;
  for (let c = 0; c < l.count; c++) {
    const f = lerp(l.fLo, l.fHi, rng()) * fm;
    const period = pickIn(rng, l.period);
    let t = rng() * period;
    const ph = { v: 0 };
    while (t < span) {
      const pulses = Math.round(pickIn(rng, l.pulses));
      const pd = 1 / l.pulseHz;
      for (let p = 0; p < pulses; p++) {
        const start = Math.round((t + p * pd) * sr);
        const len = Math.round(pd * 0.6 * sr);
        const pf = f * (1 + (l.sweep ?? 0) * (rng() - 0.3));
        for (let i = 0; i < len && start + i < out.length; i++) {
          const u = i / len;
          const fi = pf * (1 + (l.sweep ?? 0) * u);
          ph.v = (ph.v + fi / sr) % 1;
          out[start + i]! += Math.sin(2 * Math.PI * ph.v) * Math.pow(Math.sin(Math.PI * u), 0.8) * l.amp;
        }
      }
      t += period * (0.8 + 0.4 * rng());
    }
  }
}

function addWater(out: Float32Array, sr: number, l: WaterLayer, rng: Rng): void {
  const pink = noiseSource(rng, "pink");
  const lp = new Svf(sr);
  const vn = valueNoise(rng, 0.6);
  for (let i = 0; i < out.length; i++) {
    if ((i & 31) === 0) lp.set(300 + 500 * vn(i / sr), 0.8);
    out[i]! += lp.lp(pink()) * l.amp * 0.8;
  }
  const span = out.length / sr;
  let t = 0;
  for (;;) {
    t += -Math.log(1 - rng() * 0.999) / l.bubbles;
    if (t > span) break;
    const start = Math.round(t * sr);
    const f = lerp(350, 1300, rng());
    const tau = 0.01 + 0.02 * rng();
    const len = Math.round(tau * 6 * sr);
    let ph = 0;
    for (let i = 0; i < len && start + i < out.length; i++) {
      const tt = i / sr;
      ph = (ph + (f * (1 + (tt / tau) * 0.35)) / sr) % 1;
      out[start + i]! += Math.sin(2 * Math.PI * ph) * Math.exp(-tt / tau) * l.amp * 0.5 * rng();
    }
  }
}

function addMurmur(out: Float32Array, sr: number, l: MurmurLayer, rng: Rng): void {
  for (let v = 0; v < l.voices; v++) {
    const src = noiseSource(rng, "pink");
    const f1 = new Svf(sr);
    const f2 = new Svf(sr);
    const syl = valueNoise(rng, 3 + 2 * rng());
    const talk = valueNoise(rng, 0.15);
    const base = 450 + 250 * rng();
    for (let i = 0; i < out.length; i++) {
      const t = i / sr;
      if ((i & 63) === 0) {
        f1.set(base + 200 * syl(t + 7), 4);
        f2.set(base * 2.6 + 400 * syl(t + 11), 5);
      }
      const x = src();
      const e = Math.max(0, syl(t) - 0.35) * (talk(t) > 0.4 ? 1 : 0.15);
      out[i]! += (f1.bp(x) + 0.5 * f2.bp(x)) * e * l.amp;
    }
  }
}

/** Render one variant of a recipe: layers summed per channel, then finished (DC block, fades, tail trim or loop seam,
 *  loudness normalisation). Deterministic: the cue id and variant seed every random draw. `transpose` (semitones)
 *  renders it at another pitch (an instrument's multisample roots); `keep` holds the full length, untrimmed (a
 *  sustained instrument whose middle loops). */
export function renderSound(id: string, r: SoundRecipe, variant = 0, transpose = 0, keep = false): Rendered {
  const sr = r.sr ?? 32000;
  const n = Math.max(16, Math.round(r.length * sr));
  const chs: Float32Array[] = [];
  const nch = r.stereo ? 2 : 1;
  const base = variantRng(id, variant);
  const pitch = Math.pow(2, transpose / 12);
  const fm = (1 + (base() * 2 - 1) * (r.jitter ?? 0.04) * (variant === 0 ? 0 : 1)) * pitch;
  for (let c = 0; c < nch; c++) {
    const rng = variantRng(`${id}#${c}`, variant);
    const out = new Float32Array(n);
    for (const l of r.layers) {
      switch (l.type) {
        case "noise":
          addNoise(out, sr, l, rng, fm);
          break;
        case "tone":
          addTone(out, sr, l, rng, fm);
          break;
        case "modal":
          addModal(out, sr, l, rng, fm);
          break;
        case "grains":
          addGrains(out, sr, l, rng, fm);
          break;
        case "crackle":
          addCrackle(out, sr, l, rng, fm);
          break;
        case "notes":
          addNotes(out, sr, l, rng, pitch);
          break;
        case "pluck":
          addPluck(out, sr, l, rng, fm);
          break;
        case "pad":
          addPad(out, sr, l, rng, fm);
          break;
        case "fm":
          addFm(out, sr, l, rng, fm);
          break;
        case "warmpad":
          addWarmPad(out, sr, l, rng, fm);
          break;
        case "wind":
          addWind(out, sr, l, rng);
          break;
        case "rain":
          addRain(out, sr, l, rng);
          break;
        case "chirps":
          addChirps(out, sr, l, rng, fm);
          break;
        case "water":
          addWater(out, sr, l, rng);
          break;
        case "murmur":
          addMurmur(out, sr, l, rng);
          break;
      }
    }
    dcBlock(out, sr);
    chs.push(out);
  }
  let finished: Float32Array[];
  if (r.loop) finished = chs.map((x) => loopSeam(x, sr, r.loop!));
  else {
    for (const x of chs) fades(x, sr);
    finished = keep ? chs : trimTail(chs, sr);
  }
  if (r.level !== "none") normalise(finished, sr, r.loop ? "long" : "short", typeof r.level === "number" ? r.level : r.loop ? -20 : -16);
  for (const x of finished) for (let i = 0; i < x.length; i++) if (!Number.isFinite(x[i]!)) x[i] = 0;
  return { sr, channels: finished };
}

/** Problems with a recipe as data (an unknown layer type, a non-positive length or a frequency out of range). */
export function checkRecipe(r: SoundRecipe): string[] {
  const out: string[] = [];
  const types = new Set(["noise", "tone", "modal", "grains", "crackle", "notes", "pluck", "pad", "fm", "warmpad", "wind", "rain", "chirps", "water", "murmur"]);
  if (!(r.length > 0)) out.push("length must be positive");
  if (!r.layers.length) out.push("no layers");
  for (const l of r.layers) {
    if (!types.has(l.type)) out.push(`unknown layer type "${(l as { type: string }).type}"`);
    const amp = (l as { amp?: unknown }).amp;
    if (!(typeof amp === "number" && Number.isFinite(amp))) out.push(`${l.type}: no amp (a silent layer)`);
    const f = (l as { f0?: number }).f0;
    if (f !== undefined && !(f > 0 && f < 24000)) out.push(`${l.type}: f0 ${f} out of range`);
  }
  return out;
}
